import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, select, takeLatest, takeLeading } from "redux-saga/effects";
import { API_URL } from "utils/constants";
import { getPaginationMeta } from "utils/helpers/pagination";
import { request } from "utils/request";

import {
  BodyTypeForm,
  BodyTypeListResponse,
  BodyTypeUpdatePayload,
  BodyTypeUpdateResponse,
} from "../types";
import { selectForm, selectPage, selectPerPage } from "./selector";
import { actions } from "./slice";

let isFetchingList = false;

export function* handleGetList() {
  if (isFetchingList) return;
  isFetchingList = true;

  try {
    const page: number = yield select(selectPage);
    const perPage: number = yield select(selectPerPage);

    const response: BodyTypeListResponse = yield call(
      request,
      `${API_URL}/body-type/list?page=${page}&perPage=${perPage}`,
      { method: "GET" },
    );

    const data = response?.data || [];
    const meta = getPaginationMeta(page, perPage, data, response?.meta);
    const payload = { data, meta };

    if (page === 1) {
      yield put(actions.getListSuccess(payload));
    } else {
      yield put(actions.getNextPageItemsSuccess(payload));
    }
  } catch (error) {
    const message = (error as Error).message;
    const page: number = yield select(selectPage);

    if (page === 1) {
      yield put(actions.getListError(message));
    } else {
      yield put(actions.getNextPageItemsError(message));
    }
  } finally {
    isFetchingList = false;
  }
}

export function* handleAdd() {
  const form: BodyTypeForm = yield select(selectForm);

  try {
    yield call(request, `${API_URL}/body-type`, {
      method: "POST",
      body: JSON.stringify({
        bodyTypeName: form.bodyTypeName.trim(),
      }),
    });

    yield put(actions.addSuccess());
    yield put(actions.getList());
  } catch (error) {
    yield put(actions.addError((error as Error).message));
  }
}

export function* handleUpdate(action: PayloadAction<BodyTypeUpdatePayload>) {
  const { id, bodyTypeName } = action.payload;

  try {
    const response: BodyTypeUpdateResponse = yield call(
      request,
      `${API_URL}/body-type/${id}`,
      {
        method: "PATCH",
        body: JSON.stringify({
          bodyTypeName: bodyTypeName.trim(),
        }),
      },
    );

    const data = response?.data;

    yield put(
      actions.updateSuccess({
        bodyTypeId: data?.bodyTypeId ?? id,
        bodyTypeName: data?.bodyTypeName ?? bodyTypeName,
      })
    );
  } catch (error) {
    yield put(actions.updateError((error as Error).message));
  }
}

export function* handleDelete(action: PayloadAction<number>) {
  const id = action.payload;

  try {
    yield call(request, `${API_URL}/body-type/${id}`, {
      method: "DELETE",
    });

    yield put(actions.deleteSuccess(id));
  } catch (error) {
    yield put(actions.deleteError((error as Error).message));
  }
}

export function* bodyTypeSaga() {
  yield takeLeading(
    [actions.getList.type, actions.getNextPageItems.type],
    handleGetList,
  );
  yield takeLatest(actions.add.type, handleAdd);
  yield takeLatest(actions.update.type, handleUpdate);
  yield takeLatest(actions.delete.type, handleDelete);
}

export default bodyTypeSaga;
